import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { WidgetLayoutResponse } from '@frontend/sports/types/components/widget';
import { Observable, combineLatest, forkJoin, map, switchMap, take, tap } from 'rxjs';

import { WidgetContextService } from '../widget/common/widget-context.service';
import { CompetitionListSeoService } from './competition-list-seo.service';
import { CompetitionRouteService } from './competition-route.service';
import { CompetitionOverview } from './services/competition-list.service';
import { CompetitionService } from './services/competition.service';

@Injectable({ providedIn: 'root' })
export class CompetitionPageResolver {
    readonly #competitionRouteService = inject(CompetitionRouteService);
    readonly #competitionService = inject(CompetitionService);
    readonly #widgetContextService = inject(WidgetContextService);
    readonly #seoService = inject(CompetitionListSeoService);

    resolve(route: ActivatedRouteSnapshot): Observable<WidgetLayoutResponse> {
        if (this.#competitionService.getRouteParams) {
            route.params = this.#competitionService.getRouteParams();
        }

        const enchancedContext$ = this.#widgetContextService
            .enhanceContext(this.#widgetContextService.getContext({ snapshot: route }))
            .pipe(tap((ctx) => this.#seoService.saveRealCompetitionId(ctx.realCompetitionId)));

        const masterData$ = this.#competitionService.getData(route);
        const layout$ = enchancedContext$.pipe(switchMap((ctx) => this.#widgetContextService.getLayout(ctx, false)));

        return forkJoin([layout$, masterData$]).pipe(
            map(([layout, data]) => {
                this.handleMasterData(data);

                return layout;
            }),
        );
    }

    preResolve(route: ActivatedRouteSnapshot): void {
        // Extract Speculative Link url from preResolver and use it to override the params
        route.params = this.#competitionRouteService.parse((route as any).preResolverUrl as string);
        combineLatest([this.#competitionService.getPreResolvedData(route), this.#widgetContextService.preResolveWidgets(route)])
            .pipe(take(1))
            .subscribe();
    }

    private handleMasterData(data: CompetitionOverview | undefined): void {
        if (data) {
            this.#competitionService.registerDependency(data);
        }
    }
}
